import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import Avatar from '../gui/avatar';

/**
 * Hall of fame: cross-clinic staff leaderboards, opened from the Kunde Admin
 * page (/dyrejournal-admin). Loads GET /api/halloffame/, which is super-admin
 * only.
 *
 * Boards rank PEOPLE, not accounts — a vet with accounts at three clinics
 * appears once, with the clinic they used most recently. Internal accounts
 * (admin/support) and test clinics are excluded by default, because they
 * dwarf every real clinic; the toggle brings them back.
 *
 * "Aktivitet" counts logged POST/PATCH/DELETE calls. GETs are never logged, so
 * it measures actions taken rather than pages viewed — the UI says so rather
 * than implying it is a full usage metric.
 */

const PERIODS = [
    { key: '30d', label: <Text id='halloffame.period-30d'>30 days</Text> },
    { key: '90d', label: <Text id='halloffame.period-90d'>90 days</Text> },
    { key: 'year', label: <Text id='halloffame.period-year'>1 year</Text> },
];

// While the activity history backfills, the route builds a slice per request.
const BACKFILL_POLL_MS = 1500;

function formatNumber(value) {
    if (value === undefined || value === null) {
        return '0';
    }
    return Math.round(value).toLocaleString('nb-NO');
}

function formatAmount(value) {
    return `${formatNumber(value)} kr`;
}

function formatRatio(value) {
    if (!value) {
        return '0';
    }
    return value.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function medalClass(rank) {
    if (rank === 1) {
        return 'text-warning';
    }
    if (rank === 2) {
        return 'text-body-secondary';
    }
    if (rank === 3) {
        return 'text-danger';
    }
    return '';
}

@observer
class HallOfFameDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: null,
            isLoading: true,
            error: null,
            period: '30d',
            includeInternal: false,
        };
        this.pollTimer = null;
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.clearPoll();
    }

    clearPoll = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    load = async () => {
        const { period, includeInternal } = this.state;
        this.clearPoll();
        this.setState({ isLoading: true });
        try {
            const query = `period=${period}&includeInternal=${includeInternal ? 1 : 0}`;
            const response = await util.fetchApi(`/api/halloffame/?${query}`, { method: 'GET' });
            if (response.status === 200) {
                this.setState({ data: response.data, isLoading: false, error: null });
                // The route only builds a slice of the missing history per
                // request so it never times out; keep asking until it is done.
                if (response.data && response.data.pendingDays > 0) {
                    this.pollTimer = setTimeout(this.load, BACKFILL_POLL_MS);
                }
            } else {
                const message = response.message || 'Failed to load hall of fame';
                this.setState({ error: message, isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message, isLoading: false });
        }
    }

    handlePeriodClick = (e) => {
        const { period } = e.currentTarget.dataset;
        this.setState({ period }, this.load);
    }

    handleInternalToggle = (e) => {
        const includeInternal = e.target.checked;
        this.setState({ includeInternal }, this.load);
    }

    renderRow(row, max, format) {
        const width = max > 0 ? Math.max(2, Math.round((row.value / max) * 100)) : 0;
        return (
            <div class='list-group-item d-flex align-items-center gap-3' key={row.personKey}>
                <div class='text-center' style='width: 2rem;'>
                    {row.rank <= 3 ? (
                        <i class={`fa-solid fa-medal ${medalClass(row.rank)}`} />
                    ) : (
                        <span class='text-body-secondary small'>{row.rank}</span>
                    )}
                </div>
                <Avatar name={row.name} image={row.image} size={32} />
                <div class='flex-grow-1 min-width-0'>
                    <div class='text-truncate'>{row.name}</div>
                    <div class='small text-body-secondary text-truncate'>
                        {row.customerName}
                        {row.accounts > 1 && (
                            <span class='ms-1'>
                                <Text id='halloffame.accounts' fields={{ count: row.accounts }}>
                                    ({row.accounts} accounts)
                                </Text>
                            </span>
                        )}
                    </div>
                    <div class='progress mt-1' style='height: 4px;'>
                        <div class='progress-bar' role='progressbar' style={`width: ${width}%;`} />
                    </div>
                </div>
                <div class='text-end'>
                    <div class='fw-bold'>{format(row)}</div>
                    {row.secondary > 0 && (
                        <div class='small text-body-secondary'>
                            <Text id='halloffame.saleCount' fields={{ count: formatNumber(row.secondary) }}>
                                {formatNumber(row.secondary)} sales
                            </Text>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderBoard(title, icon, help, rows, format) {
        const max = rows.length > 0 ? rows[0].value : 0;
        return (
            <div class='card mb-4'>
                <div class='card-header'>
                    <i class={`fa-solid ${icon} me-2`} />
                    {title}
                    <div class='small text-body-secondary mt-1'>{help}</div>
                </div>
                {rows.length === 0 ? (
                    <div class='card-body text-body-secondary'>
                        <Text id='halloffame.noData'>No data for this period.</Text>
                    </div>
                ) : (
                    <div class='list-group list-group-flush'>
                        {rows.map(row => this.renderRow(row, max, format))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const {
            data, isLoading, error, period, includeInternal,
        } = this.state;

        if (isLoading && !data) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!data) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>
                        <Text id='halloffame.noData'>No data for this period.</Text>
                    </div>
                </div>
            );
        }

        const { boards = {}, naggerFloors = {}, warnings = [] } = data;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding: 20px; padding-bottom: 100px;'>

                    <div class='mb-3'>
                        <h5 class='mb-0'>
                            <i class='fa-solid fa-trophy me-2 text-warning' />
                            <Text id='halloffame.title'>Hall of fame</Text>
                        </h5>
                        <div class='small text-body-secondary'>
                            <Text id='halloffame.subtitle'>Staff across all clinics</Text>
                        </div>
                    </div>

                    <div class='d-flex flex-wrap align-items-center gap-3 mb-4'>
                        <div class='btn-group btn-group-sm' role='group'>
                            {PERIODS.map(p => (
                                <button
                                    key={p.key}
                                    type='button'
                                    class={`btn ${period === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    data-period={p.key}
                                    onClick={this.handlePeriodClick}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <div class='form-check form-switch mb-0'>
                            <input
                                class='form-check-input'
                                type='checkbox'
                                id='hofIncludeInternal'
                                checked={includeInternal}
                                onChange={this.handleInternalToggle}
                            />
                            <label class='form-check-label small' for='hofIncludeInternal'>
                                <Text id='halloffame.includeInternal'>Show internal accounts</Text>
                            </label>
                        </div>
                        {isLoading && (
                            <span class='spinner-border spinner-border-sm text-primary' role='status' />
                        )}
                    </div>

                    {data.pendingDays > 0 && (
                        <div class='alert alert-info d-flex align-items-center gap-2'>
                            <span class='spinner-border spinner-border-sm' role='status' />
                            <Text id='halloffame.buildingHistory' fields={{ days: data.pendingDays }}>
                                Building activity history… {data.pendingDays} days left.
                            </Text>
                        </div>
                    )}

                    {warnings.length > 0 && (
                        <div class='alert alert-warning'>
                            <Text id='halloffame.warning' fields={{ boards: warnings.join(', ') }}>
                                Some data could not be loaded: {warnings.join(', ')}
                            </Text>
                        </div>
                    )}

                    {this.renderBoard(
                        <Text id='halloffame.activity'>Most active</Text>,
                        'fa-bolt',
                        <Text id='halloffame.activity-help'>
                            Registered actions (new, changed and deleted records). Page views are not counted.
                        </Text>,
                        boards.activity || [],
                        row => formatNumber(row.value),
                    )}

                    {this.renderBoard(
                        <Text id='halloffame.sales'>Most sales</Text>,
                        'fa-cash-register',
                        <Text id='halloffame.sales-help'>Turnover including VAT, summed from the sale lines.</Text>,
                        boards.sales || [],
                        row => formatAmount(row.value),
                    )}

                    {this.renderBoard(
                        <Text id='halloffame.journals'>Most journals</Text>,
                        'fa-notes-medical',
                        <Text id='halloffame.journals-help'>Journals per attending veterinarian.</Text>,
                        boards.journals || [],
                        row => formatNumber(row.value),
                    )}

                    {this.renderBoard(
                        <Text id='halloffame.tickets'>Most support tickets</Text>,
                        'fa-life-ring',
                        <Text id='halloffame.tickets-help'>Tickets created in the period.</Text>,
                        boards.tickets || [],
                        row => formatNumber(row.value),
                    )}

                    {this.renderBoard(
                        <Text id='halloffame.nagger'>Biggest nagger</Text>,
                        'fa-comment-dots',
                        <Text
                            id='halloffame.nagger-help'
                            fields={{ minTickets: naggerFloors.minTickets, minActions: naggerFloors.minActions }}
                        >
                            Tickets per 1000 actions — many tickets, little usage. Needs at least
                            {' '}{naggerFloors.minTickets} tickets and {naggerFloors.minActions} actions.
                        </Text>,
                        boards.nagger || [],
                        row => (
                            <span>
                                {formatRatio(row.value)}
                                <span class='small text-body-secondary'>/1000</span>
                            </span>
                        ),
                    )}

                </div>
            </div>
        );
    }
}

export default HallOfFameDrawer;
